import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image, message, Modal, Form } from 'antd';
import { AutoComplete } from 'antd';
import { ToolOutlined, ExclamationCircleOutlined, ThunderboltOutlined, HomeOutlined, ProductOutlined, TruckOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import EditableDrawer from 'components/EditableDrawer';
import SingleSelect from 'components/SingleSelect';
import InputFilterItem from 'components/InputFilterItem';
import DriverPhoneRow, { isPhoneInvalid, checkDriverPhoneDuplicate } from './DriverPhoneRow';
import { GET_TMS_TASK_DETAIL, GetTmsTaskDetailResponse, GetTmsTaskDetailVariables } from '../graphql/queries/getTmsTaskDetail';
import { GET_HUB_LIST, GetHubListResponse } from '../../../wms/bags/graphql/queries/getHubList';
import { UPDATE_TMS_TASK, UpdateTmsTaskInput, UpdateTmsTaskResponse, TmsTaskTemporaryDriverInput, TmsTaskTemporaryVehicleInput, TmsTaskTransitInfoInput, UPDATE_TMS_TASK_TRANSIT_INFOS, UpdateTmsTaskTransitInfosResponse, UPDATE_TMS_TASK_BULK_CONSOLIDATED, UpdateTmsTaskBulkConsolidatedResponse} from '../graphql/mutations/taskMutations';
import EditToolbar from './EditToolbar';
import { useTmsDriversByCarrier } from '../../hooks/useTmsDriversByCarrier';
import { useTmsVehicleAndModel } from '../../hooks/useTmsVehicleAndModel';
import { useTmsVehiclesByCarrier } from '../../hooks/useTmsVehiclesByCarrier';
import {
    TmsTaskStatusEnum,
    TmsTaskTypeEnum,
    ExceptionReasonEnum,
    TmsRouteTypeEnum,
    TmsBusinessTypeEnum,
    WaypointTypeEnum,
    TmsTaskWaypointStatusEnum,
} from 'enums/TmsEnum';
import TransitSection, { IS_TRANSIT } from './TransitSection';
import { TransitInfoRow } from './TransitInfoTable';
import LoadingDetailDrawer from './LoadingDetailDrawer';
import UnloadingDetailDrawer from './UnloadingDetailDrawer';
import TaskWaypointDetail from './TaskWaypointDetail';
import dayjs from 'dayjs';
import { t } from '../../../../i18n';
import { showError } from '../../../../utils/exceptionUtils';
import { parseResultCode, parseResultArgs } from '../../../../utils/resultCode';
import { CHECK_ACCOUNT_BY_PHONE, CheckAccountByPhoneResponse } from '../graphql/queries/checkAccountByPhone';
import { useFilterLayoutStyles } from '../../../../utils/languageUtils';
import TmsTaskConfirmDriverVehicleModal from './TmsTaskConfirmDriverVehicleModal';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import { FuelTypeEnum, getFuelTypeLabel } from '../../../../enums/FuelTypeEnum';
import { GET_VEHICLES, VehiclesResponse } from '../../vehicle/graphql/queries/getVehicles';
import { FIND_VEHICLE_TYPE_BY_ID, FindVehicleTypeByIdResponse } from '../../../../graphql/queries/getAllVehicleTypes';
import { formatCentsToDecimalString, parseDecimalStringToCents, sanitizeDecimalInput } from '../utils/perVehicleCost';
import { escortCountOptions } from '../utils/escortCount';

interface EditTaskDrawerProps {
    open: boolean;
    onClose: () => void;
    onSubmit?: () => void;
    taskId: string | null;
    mode: 'edit' | 'view';
}

interface FilterOptions {
    routeId: string;
    taskDate: string;
    originPlannedDepartureTime: string;
    taskType: TmsTaskTypeEnum;
    businessType: string;
    // 司机1：必填（交接类型除外）
    driver1Id: string;
    driver1Name: string;
    driver1PhoneNumber: string;
    // 司机2：非必填，仅BOL展示，不参与派单/APP/结算
    driver2Id: string;
    driver2Name: string;
    driver2PhoneNumber: string;
    vehicleId: string;
    licensePlate: string;
    vehicleModel: string;
    vehicleTypeId: number | null;
    escortCount: string;
    perVehicleCost: string;
    gpsTracking: string;
    trailerNumber: string;
    remark: string;
    status: TmsTaskStatusEnum;
    isTransit: string; // 是否转运，取值见 IS_TRANSIT
    isBulkConsolidated: boolean; // 是否散货拼车
}

const getTaskTypeLabelKey = (value: TmsTaskTypeEnum | number | null | undefined): string | undefined => {
    if (value == null) return undefined;
    const v = Number(value);
    if (v === TmsTaskTypeEnum.Regular) return 'tms.task.taskTypes.regular';
    if (v === TmsTaskTypeEnum.Temporary) return 'tms.task.taskTypes.temporary';
    if (v === TmsTaskTypeEnum.ThirdParty) return 'tms.task.taskTypes.thirdParty';
    return undefined;
};

const EditTaskDrawer: React.FC<EditTaskDrawerProps> = ({
    open,
    onClose,
    onSubmit,
    taskId,
    mode,
}) => {
    const isEditMode = mode === 'edit';

    // 查询任务详情
    const { data: taskDetailData } = useQuery<GetTmsTaskDetailResponse, GetTmsTaskDetailVariables>(
        GET_TMS_TASK_DETAIL,
        {
            variables: {
                taskId: taskId!!
            },
            skip: !taskId || !open,
            fetchPolicy: 'cache-and-network' // 支持 refetchQueries 更新后组件能拿到新数据
        }
    );

    const task = taskDetailData?.task;
    const waypoints = taskDetailData?.waypoints || [];
    const exceptions = taskDetailData?.exceptions || [];

    // 标题永远显示任务号，不区分编辑/查看模式
    const drawerTitle = task?.taskNumber!!;

    // 创建 waypointId 到 sortingCode 的映射（用于 UnloadingDetailDrawer）
    const waypointIdToSortingCodeMap = useMemo(() => {
        const map = new Map<number, string>();
        waypoints.forEach(wp => {
            if (wp.waypointId && wp.waypointSortingCode) {
                map.set(wp.waypointId, wp.waypointSortingCode);
            }
        });
        return map;
    }, [waypoints]);

    // 注意：exceptionReasonType是Int类型，对应ExceptionReasonEnum枚举值
    // 这里暂时显示ID，后续可以通过后端API获取异常原因名称

    // 是否为交接类型路线（用 ref 缓存，destroyOnClose 销毁内容时 task 已为 null，避免 label 闪跳）
    const isHandoverRouteRef = useRef(false);
    if (task) {
        isHandoverRouteRef.current = task.routeType === TmsRouteTypeEnum.Handover;
    }
    const isHandoverRoute = isHandoverRouteRef.current;

    // 判断是否需要单车成本：交接类型不需要
    const shouldIncludePerVehicleCost = !isHandoverRoute;

    // 使用Hook根据承运商ID获取司机（含 phoneNumber，用于根据 driver 匹配联系方式，交接路线无需查询）
    const { driverOptions, driversData } = useTmsDriversByCarrier(isHandoverRoute ? null : task?.carrierId);
    // 司机ID → 电话，供 DriverPhoneRow 选中系统司机后自动带出电话
    const driverPhoneById = useMemo(
        () => Object.fromEntries(
            (driversData?.getAccountTmsDriversByCarrierId ?? []).map(d => [d.id.toString(), d.phoneNumber])
        ),
        [driversData]
    );
    // 按手机号检查账号是否存在（编辑任务时校验联系方式重复性）
    const [checkAccountByPhoneQuery] = useLazyQuery<CheckAccountByPhoneResponse>(CHECK_ACCOUNT_BY_PHONE);
    // 按车牌号查询车辆（自由编辑车辆时弹窗前校验是否已绑定其他承运商）
    const [getVehiclesByLicensePlate] = useLazyQuery<VehiclesResponse>(GET_VEHICLES);
    const [findVehicleTypeById] = useLazyQuery<FindVehicleTypeByIdResponse>(FIND_VEHICLE_TYPE_BY_ID);

    // 使用Hook根据carrierId自动查询车辆数据（交接路线无需查询）
    const { vehiclesData, vehicleOptions, vehiclesLoading: driverVehicleLoading } = useTmsVehiclesByCarrier(isHandoverRoute ? null : task?.carrierId, !open);
    const carrierName = task?.carrierName || '';
    const { vehicleTypes } = useVehicleTypes();
    const vehicleModelOptions = useMemo(() => {
        return (vehicleTypes || [])
            .map(vt => ({
                label: vt.nameEn,
                value: vt.id  // number
            }));
    }, [vehicleTypes]);
    const fuelTypeOptions = useMemo(() => {
        return Object.values(FuelTypeEnum)
            .filter((v): v is number => typeof v === 'number')
            .map(v => ({ label: getFuelTypeLabel(v, t), value: v }));
    }, [t]);
    const statusOptions = useMemo(() => [
        { value: 1, label: String(t('enum.activeStatus.active')) },
        { value: 0, label: String(t('enum.activeStatus.suspended')) }
    ], [t]);

    // 转运信息：仅「干线 + 自有路线」任务展示
    const showTransit = task?.routeType === TmsRouteTypeEnum.Linehaul
        && task?.businessType === TmsBusinessTypeEnum.OwnRoute;
    // 转运行数据（由 TransitSection 维护、保存时提交）
    const [transitRows, setTransitRows] = useState<TransitInfoRow[]>([]);

    // 转运下拉候选（干线）：以 HUB list 为全集
    //  - 转运目的地站：HUB list，本路线站点(始发/途径/终点)置灰"不可用"（不剔除）
    //  - 转运站：本路线的途径站 + 终到站
    // 支线场景：目前不支持转运
    const { data: hubListData } = useQuery<GetHubListResponse>(GET_HUB_LIST, { skip: !open || !showTransit });
    const { transitStationOptions, transitDestStationOptions } = useMemo(() => {
        const hubs = hubListData?.hubList ?? [];
        const routeStationIds = new Set(waypoints.map((w) => String(w.waypointId)));
        // 转运目的地站：HUB list 剔除本路线站点(始发/途径/终点)
        const destStationOptions = hubs
            .filter((hub) => !routeStationIds.has(String(hub.id)))
            .map((hub) => ({ value: hub.id, label: hub.sortingCode! }));
        // 转运站：本路线的途径站 + 终到站。已发车站不剔除，而是 disabled+secondaryLabel 置灰不可选，
        // 这样当前值(可能已发车)仍在列表里能正常显示站点码，且只能改选未发车站。
        const stationOptions = waypoints
            .filter((waypoint) => waypoint.waypointType === WaypointTypeEnum.Waypoint || waypoint.waypointType === WaypointTypeEnum.Destination)
            .map((waypoint) => {
                const departed = waypoint.waypointStatus === TmsTaskWaypointStatusEnum.Done;
                return {
                    value: String(waypoint.waypointId),
                    label: waypoint.waypointSortingCode!,
                    disabled: departed,
                    secondaryLabel: departed ? String(t('tms.task.drawer.transitTable.stationDeparted')) : undefined,
                };
            });
        return { transitStationOptions: stationOptions, transitDestStationOptions: destStationOptions };
    }, [hubListData, waypoints]);

    // 确认弹窗司机表单的车辆下拉：复用主表单同一承运商的车辆数据（起点配车，车属于任务承运商），
    // 不再按 warehouse 维度另发一次查询——到达本站页面 task 所属仓是对方始发站，会触发仓库权限校验失败
    const driverVehicleOptions = useMemo(() =>
        (vehiclesData?.getTmsVehiclesWithModel ?? []).map(vehicle => ({
            value: vehicle.id,
            label: vehicle.licensePlate || ''
        })), [vehiclesData]);

    // 筛选框状态
    const [filters, setFilters] = useState<FilterOptions>({
        routeId: '',
        taskDate: '',
        originPlannedDepartureTime: '',
        taskType: TmsTaskTypeEnum.Regular,
        businessType: '',
        driver1Id: '',
        driver1Name: '',
        driver1PhoneNumber: '',
        driver2Id: '',
        driver2Name: '',
        driver2PhoneNumber: '',
        vehicleId: '',
        licensePlate: '',
        vehicleModel: '',
        vehicleTypeId: null,
        escortCount: '1',
        perVehicleCost: '',
        gpsTracking: '',
        trailerNumber: '',
        remark: '',
        status: TmsTaskStatusEnum.Draft,
        isTransit: IS_TRANSIT.NO,
        isBulkConsolidated: false,
    });

    // 确认弹窗里“司机选择车辆”下拉框：如果当前任务车辆不在可选列表中（例如车辆被停用），也要能显示 label 而不是显示 ID
    const confirmDriverVehicleOptions = useMemo(() => {
        const currentVehicleId =
            (filters.vehicleId ? Number(filters.vehicleId) : undefined) ??
            (task?.vehicleId != null ? Number(task.vehicleId) : undefined);
        const currentVehicleLabel = filters.licensePlate || task?.licensePlate || '';

        if (!currentVehicleId || !currentVehicleLabel) return driverVehicleOptions;
        const exists = driverVehicleOptions.some(o => Number(o.value) === currentVehicleId);
        if (exists) return driverVehicleOptions;
        return [{ label: currentVehicleLabel, value: currentVehicleId }, ...driverVehicleOptions];
    }, [driverVehicleOptions, filters.vehicleId, filters.licensePlate, task?.vehicleId, task?.licensePlate]);

    const handleFilterChange = (name: keyof FilterOptions, value: string | number | undefined | string[] | number[]) => {
        setFilters(prev => ({ ...prev, [name]: value || '' }));
    };

    // 标记 vehicle 是否是从司机查询自动设置的
    const [isVehicleAutoSet, setIsVehicleAutoSet] = useState(false);
    // 记录司机绑定的车辆信息（如果司机有绑定车辆）
    const [driver1BoundVehicle, setDriver1BoundVehicle] = useState<{ id: number; licensePlate?: string; vehicleNumber?: string } | null>(null);
    // 标记 driver/vehicle 是否为自由输入（不在列表中）
    const [isDriver1FreeInput, setIsDriver1FreeInput] = useState(false);
    const [isVehicleFreeInput, setIsVehicleFreeInput] = useState(false);
    /**
     * 本次打开抽屉期间用户手打过司机2 姓名（由 DriverPhoneRow 的 onDriverCleared 置位），与司机1 同一套口径。
     * 不能只看 filters 派生：任务原本就存着手输的临时司机时，driver2Id 匹配不到系统司机会被置空
     * （见回显处），光凭「有姓名无 ID」会把回显当成手输，重开编辑就重复建号、并被重号检测误拦。
     */
    const [driver2TypedByUser, setDriver2TypedByUser] = useState(false);
    // 跟车人数为1时仅司机1；>1才需要司机2
    // 兼容历史数据：跟车人数=1但已存在司机2（历史录入不一致）时仍展示，避免脏数据被隐藏后无法查看/清理
    const showDriver2 = Number(filters.escortCount) > 1 || !!filters.driver2Id || !!filters.driver2Name;

    /**
     * 司机2 是手输（本次手打了姓名且没选中系统司机）→ 同样要建司机账号，走确认弹窗。
     * 交接类型排除：司机是对方承运商的人，系统里没账号，与司机1 口径一致。
     */
    const isDriver2FreeInput = showDriver2 && !isHandoverRoute && driver2TypedByUser && !!filters.driver2Name && !filters.driver2Id;
    // 同一个人不能既是司机1 又是司机2：各自下拉里去掉对方已选中的司机（两个方向都挡，不管先选哪个）
    const driver1Options = useMemo(
        () => driverOptions.filter(o => o.value !== filters.driver2Id),
        [driverOptions, filters.driver2Id]
    );
    const driver2Options = useMemo(
        () => driverOptions.filter(o => o.value !== filters.driver1Id),
        [driverOptions, filters.driver1Id]
    );
    // 标记 phoneNumber 是否来自司机选中回填（回填时置灰不可编辑，与 CreateTemporaryTaskDrawer 一致）
    const [isDriver1PhoneAutoFilled, setIsDriver1PhoneAutoFilled] = useState(false);
    // 司机2 同上：只在「选中系统司机并带出了电话」时置灰，与司机1 同一套口径
    const [isDriver2PhoneAutoFilled, setIsDriver2PhoneAutoFilled] = useState(false);
    // 确认弹窗（自由输入时填写司机/车辆详情）
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [confirmSubmitting, setConfirmSubmitting] = useState(false);
    const [driver1Form] = Form.useForm();
    const [driver2Form] = Form.useForm();
    const [vehicleForm] = Form.useForm();
    const { containerClassName, labelClassName, inputContainerClassName } = useFilterLayoutStyles('w-[7em]', undefined, true);

    // Drawer 关闭/重新打开时重置“自由输入”相关状态，避免二次打开沿用上一次状态导致误弹窗
    useEffect(() => {
        if (!open) {
            setConfirmModalVisible(false);
            setConfirmSubmitting(false);
            setIsDriver1FreeInput(false);
            setDriver2TypedByUser(false);
            setIsVehicleFreeInput(false);
            setIsVehicleAutoSet(false);
            setDriver1BoundVehicle(null);
            setIsDriver1PhoneAutoFilled(false);
            setIsDriver2PhoneAutoFilled(false);
            setRouteInfoEditing(false); // 复位“路线信息”区块独立编辑态（转运+散货拼车共用），避免下次打开仍处于编辑
            driver1Form.resetFields();
            driver2Form.resetFields();
            vehicleForm.resetFields();
        }
    }, [open, driver1Form, driver2Form, vehicleForm]);

    // 使用Hook查询车辆和车型信息
    const { getVehicleAndModel } = useTmsVehicleAndModel({
        onVehicleAndModelFound: (vehicleId, vehicleModelName, _vehicleModelId, vehicle, vehicleTypeId) => {
            setFilters(prev => ({
                ...prev,
                vehicleId: vehicleId.toString(),
                licensePlate: vehicle.licensePlate || '',
                vehicleModel: vehicleModelName,
                vehicleTypeId: vehicleTypeId || null,
            }));
            // 标记为自动设置，禁用 Vehicle 和 Vehicle Model
            setIsVehicleAutoSet(true);
            // 记录司机绑定的车辆信息
            setDriver1BoundVehicle(vehicle);
        },
        onVehicleAndModelNotFound: () => {
            // 如果返回 null，说明该司机没有分配车辆，清空车辆和车型，允许手动选择 Vehicle
            setFilters(prev => ({ ...prev, vehicleId: '', licensePlate: '', vehicleModel: '', vehicleTypeId: null }));
            setIsVehicleAutoSet(false);
            setDriver1BoundVehicle(null);
        },
        onError: () => {
            // 出错时也允许手动选择
            setFilters(prev => ({ ...prev, vehicleId: '', licensePlate: '', vehicleModel: '', vehicleTypeId: null }));
            setIsVehicleAutoSet(false);
            setDriver1BoundVehicle(null);
        }
    });

    const handleVehicleTypeChange = async (vehicleTypeId: number) => {
        try {
            const result = await findVehicleTypeById({ variables: { id: vehicleTypeId } });
            const vehicleType = result.data?.findVehicleTypeById;
            if (vehicleType) {
                vehicleForm.setFieldsValue({
                    maxWeightLbs: vehicleType.defaultMaxWeightLbs ?? undefined,
                    maxVolumeFt3: vehicleType.defaultMaxVolumeFt3 ?? undefined,
                    lengthFt: vehicleType.defaultLengthFt ?? undefined,
                    widthFt: vehicleType.defaultWidthFt ?? undefined,
                    heightFt: vehicleType.defaultHeightFt ?? undefined,
                });
            }
        } catch (error) {
            console.error('Failed to fetch vehicle type details:', error);
        }
    };

    // 确认弹窗打开后，用 options 中的 value 重设司机表单的 vehicleId，保证 Select 显示 label 而非 ID（与 CreateTemporaryTaskDrawer 一致）
    useEffect(() => {
        if (!confirmModalVisible || isVehicleFreeInput) return;
        // 注意：这里不能用 task.vehicleId 兜底，否则“选择司机后清空车辆”的场景下
        // 弹窗会错误回填旧任务车辆。
        const effectiveVehicleId = filters.vehicleId ? Number(filters.vehicleId) : undefined;
        const matchedOption = effectiveVehicleId != null && confirmDriverVehicleOptions.find(
            o => Number(o.value) === effectiveVehicleId
        );
        const vehicleIdValue = matchedOption ? matchedOption.value : effectiveVehicleId;

        // 不传 password：setFieldsValue 只覆盖传入的字段，密码框保留用户已输入的值
        if (isDriver1FreeInput) {
            driver1Form.setFieldsValue({
                name: filters.driver1Name,
                phone: { countryCode: 1, phoneNumber: filters.driver1PhoneNumber || '' },
                role: 'TMS_DRIVER',
                status: 1,
                vehicleId: vehicleIdValue
            });
        }
        // 司机2 不绑定车辆，故不回填车辆字段
        if (isDriver2FreeInput) {
            driver2Form.setFieldsValue({
                name: filters.driver2Name,
                phone: { countryCode: 1, phoneNumber: filters.driver2PhoneNumber || '' },
                role: 'TMS_DRIVER',
                status: 1
            });
        }
    }, [confirmModalVisible, isDriver1FreeInput, isDriver2FreeInput, isVehicleFreeInput, filters.driver1Name, filters.driver1PhoneNumber, filters.driver2Name, filters.driver2PhoneNumber, filters.vehicleId, confirmDriverVehicleOptions, driver1Form, driver2Form]);

    const getConfirmModalTitle = (): string => {
        // 要建号的司机可能是司机1、司机2 或两个，姓名拼在同一个插值里，不为此新增文案
        const driverName = [
            isDriver1FreeInput ? filters.driver1Name : '',
            isDriver2FreeInput ? filters.driver2Name : '',
        ].filter(Boolean).join('、');
        if (driverName && isVehicleFreeInput) {
            return String(t('tms.task.confirmModal.titleBoth', { driverName, licensePlate: filters.licensePlate }));
        }
        if (driverName) {
            return String(t('tms.task.confirmModal.titleDriver', { driverName }));
        }
        if (isVehicleFreeInput) {
            return String(t('tms.task.confirmModal.titleVehicle', { licensePlate: filters.licensePlate }));
        }
        return '';
    };

    // 转运信息提交值（仅「干线+自有路线」处理）：是→全量替换；否→[] 清空；非干线→undefined 不动表
    const getTransitInfosInput = (): TmsTaskTransitInfoInput[] | undefined => {
        if (!showTransit) return undefined;
        if (filters.isTransit !== IS_TRANSIT.YES) return [];
        return transitRows
            .filter((r) => r.transitWarehouseId && r.transitDestWarehouseId)
            .map((r) => ({
                transitWarehouseId: Number(r.transitWarehouseId),
                transitDestWarehouseId: Number(r.transitDestWarehouseId),
            }));
    };

    // 确认弹窗提交：校验司机/车辆表单后，通过 updateTmsTask 的 temporaryDriver1/temporaryVehicle 一并提交（逻辑同 createTmsTask）
    const handleConfirmSubmit = async () => {
        if (!taskId || !task) return;
        setConfirmSubmitting(true);
        try {
            let temporaryDriver1: TmsTaskTemporaryDriverInput | undefined;
            let temporaryDriver2: TmsTaskTemporaryDriverInput | undefined;
            let temporaryVehicle: TmsTaskTemporaryVehicleInput | undefined;

            if (isDriver1FreeInput) {
                const driverValues = await driver1Form.validateFields();
                temporaryDriver1 = {
                    name: driverValues.name,
                    countryCode: driverValues.phone?.countryCode ?? 1,
                    phone: driverValues.phone?.phoneNumber || undefined,
                    password: driverValues.password || undefined,
                    email: driverValues.email || undefined,
                    role: 'TMS_DRIVER',
                    status: driverValues.status ?? 1,
                    carrierId: task.carrierId,
                    vehicleId: driverValues.vehicleId ? Number(driverValues.vehicleId) : undefined
                };
            }

            // 手输司机2 也建账号（不参与派单），承运商同司机1；不传 vehicleId——司机2 不绑定车辆
            if (isDriver2FreeInput) {
                const driver2Values = await driver2Form.validateFields();
                temporaryDriver2 = {
                    name: driver2Values.name,
                    countryCode: driver2Values.phone?.countryCode ?? 1,
                    phone: driver2Values.phone?.phoneNumber || undefined,
                    password: driver2Values.password || undefined,
                    email: driver2Values.email || undefined,
                    role: 'TMS_DRIVER',
                    status: driver2Values.status ?? 1,
                    carrierId: task.carrierId
                };
            }

            if (isVehicleFreeInput) {
                const vehicleValues = await vehicleForm.validateFields();
                temporaryVehicle = {
                    licensePlate: vehicleValues.licensePlate,
                    carrierId: task.carrierId,
                    vehicleTypeId: Number(vehicleValues.vehicleTypeId),
                    maxWeightLbs: vehicleValues.maxWeightLbs ?? undefined,
                    maxVolumeFt3: vehicleValues.maxVolumeFt3 ?? undefined,
                    lengthFt: vehicleValues.lengthFt ?? undefined,
                    widthFt: vehicleValues.widthFt ?? undefined,
                    heightFt: vehicleValues.heightFt ?? undefined,
                    fuelType: vehicleValues.fuelType ?? undefined,
                    status: vehicleValues.status ?? 1,
                    vehiclePhotos: vehicleValues.vehiclePhotos?.length ? vehicleValues.vehiclePhotos : undefined
                };
            }

            if (!validateDriver2Phone()) return;

            setConfirmModalVisible(false);

            const input: UpdateTmsTaskInput = {
                driver1Id: !isDriver1FreeInput && filters.driver1Id ? parseInt(filters.driver1Id) : undefined,
                driver1Name: filters.driver1Name || null,
                driver1PhoneNumber: filters.driver1PhoneNumber || null,
                // 司机2 必须一并回传：后端全量覆盖，不传会把已有司机2 清空；跟车人数<=1 时不展示司机2，强制置空
                driver2Id: showDriver2 && filters.driver2Id ? parseInt(filters.driver2Id) : null,
                driver2Name: showDriver2 ? (filters.driver2Name || null) : null,
                driver2PhoneNumber: showDriver2 ? (filters.driver2PhoneNumber || null) : null,
                vehicleId: !isVehicleFreeInput && filters.vehicleId ? parseInt(filters.vehicleId) : undefined,
                trailerNumber: filters.trailerNumber || null,
                remark: filters.remark || null,
                escortCount: Number.parseInt(filters.escortCount || '0', 10),
                perVehicleCost: shouldIncludePerVehicleCost ? parseDecimalStringToCents(filters.perVehicleCost) : undefined,
                gpsTracking: filters.gpsTracking || null,
                temporaryDriver1: temporaryDriver1 ?? undefined,
                temporaryDriver2: temporaryDriver2 ?? undefined,
                temporaryVehicle: temporaryVehicle ?? undefined,
                // 回传交接方类型，供后端判断本地尾程商交接任务可在任意状态编辑
                carrierSourceType: task?.carrierSourceType ?? null,
                transitInfos: getTransitInfosInput(),
                isBulkConsolidated: filters.isBulkConsolidated,
            };

            const res = (await updateTask({ variables: { id: taskId, input } })).data?.updateTmsTask;
            if (!res?.success) {
                message.error(parseResultCode(res?.code ?? '', res?.message ?? undefined, parseResultArgs(res?.argsJson)) || String(t('tms.task.messages.failedToUpdateTask')));
                return;
            }

            message.success(t('tms.task.messages.taskUpdatedSuccessfully'));
            onSubmit?.();
            handleClose(true); // 保存成功后跳过 filters 重置，避免用缓存的旧 task 覆盖导致输入框闪烁
        } catch (error: any) {
            if (error?.errorFields) {
                // 表单校验错误，不额外提示
            } else {
                console.error('Failed to update task:', error);
                const hasMessageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
                hasMessageKey ? showError(error) : message.error(error?.message || t('tms.task.messages.failedToUpdateTask'));
            }
        } finally {
            setConfirmSubmitting(false);
        }
    };

    // 根据 vehicleId 获取对应的车辆信息（vehicleModelName、vehicleModelId）
    const selectedVehicle = useMemo(() => {
        if (!filters.vehicleId || !vehiclesData?.getTmsVehiclesWithModel) return null;
        return vehiclesData.getTmsVehiclesWithModel.find(v => v.id.toString() === filters.vehicleId.toString()) || null;
    }, [filters.vehicleId, vehiclesData]);

    // 当手动选择 vehicle 时，自动填充 vehicleModel 和 vehicleTypeId
    useEffect(() => {
        if (!isVehicleAutoSet && filters.vehicleId && selectedVehicle) {
            setFilters(prev => ({
                ...prev,
                vehicleModel: selectedVehicle.vehicleTypeName || '',
                vehicleTypeId: selectedVehicle.vehicleTypeId || null,
            }));
        }
    }, [filters.vehicleId, selectedVehicle, isVehicleAutoSet]);

    // 初始化筛选框数据
    useEffect(() => {
        if (task) {
            const currentTransitInfos = taskDetailData?.transitInfos || [];
            // 格式化Departure Time为HH:mm格式
            let departureTime = '';
            if (task.planDepartureTime) {
                const time = dayjs(task.planDepartureTime, 'HH:mm:ss');
                departureTime = time.isValid() ? time.format('HH:mm') : task.planDepartureTime.substring(0, 5);
            }
            
            // 获取 vehicleModelName、licensePlate：优先从 tms_task 字段取，没有则从关联车辆表查
            let vehicleModelName = '';
            let licensePlate = task.licensePlate || '';
            if (task.vehicleTypeId) {
                const matched = vehicleModelOptions.find(o => String(o.value) === String(task.vehicleTypeId));
                vehicleModelName = matched?.label || '';
            } else if (task.vehicleId && vehiclesData?.getTmsVehiclesWithModel) {
                const vehicle = vehiclesData.getTmsVehiclesWithModel.find(v => v.id === task.vehicleId);
                vehicleModelName = vehicle?.vehicleTypeName || '';
                if (vehicle) licensePlate = vehicle.licensePlate || licensePlate;
            }

            // 检查 driver1Id 是否在 driverOptions 中存在，若存在用选项的 label 作为 driver1Name
            let driver1Id = '';
            let driver1Name = task.driver1Name || '';
            if (task.driver1Id) {
                const driverIdStr = task.driver1Id.toString();
                const driverOpt = driverOptions.find(option => option.value === driverIdStr);
                const driverExists = !!driverOpt;
                driver1Id = driverExists ? driverIdStr : '';
                if (driverOpt && typeof driverOpt.label === 'string') driver1Name = driverOpt.label;
            }
            // 司机2 同理：能匹配到系统司机就带上 id，自由输入的只回显姓名
            let driver2Id = '';
            let driver2Name = task.driver2Name || '';
            if (task.driver2Id) {
                const driver2IdStr = task.driver2Id.toString();
                const driver2Opt = driverOptions.find(option => option.value === driver2IdStr);
                driver2Id = driver2Opt ? driver2IdStr : '';
                if (driver2Opt && typeof driver2Opt.label === 'string') driver2Name = driver2Opt.label;
            }
            // 联系方式：优先从任务上的司机1电话取，没有则从司机账号查
            let driver1PhoneNumber = task.driver1PhoneNumber || '';
            let phoneFromDriver = false;
            if (!driver1PhoneNumber && driver1Id && driversData?.getAccountTmsDriversByCarrierId) {
                const matchedDriver = driversData.getAccountTmsDriversByCarrierId.find(
                    d => d.id.toString() === driver1Id
                );
                if (matchedDriver?.phoneNumber) {
                    driver1PhoneNumber = matchedDriver.phoneNumber;
                    phoneFromDriver = true;
                }
            }
            setIsDriver1PhoneAutoFilled(phoneFromDriver);
            // 司机2 电话直接取任务值、不回落账号，故打开时不算「选中回填」，保持可编辑（与司机1 同口径）
            setIsDriver2PhoneAutoFilled(false);
            // 打开详情时按任务数据展示，不应沿用上一次“自由输入”状态
            setIsDriver1FreeInput(false);
            setDriver2TypedByUser(false);
            setIsVehicleFreeInput(false);
            setIsVehicleAutoSet(false);
            setDriver1BoundVehicle(null);
            setFilters({
                routeId: task.routeId?.toString() || '',
                taskDate: task.taskDate || '',
                originPlannedDepartureTime: departureTime,
                taskType: task.taskType as TmsTaskTypeEnum,
                businessType: task.businessType?.toString() || '',
                driver1Id,
                driver1Name,
                driver1PhoneNumber,
                driver2Id,
                driver2Name,
                driver2PhoneNumber: task.driver2PhoneNumber || '',
                vehicleId: task.vehicleId?.toString() || '',
                licensePlate: licensePlate,
                vehicleModel: vehicleModelName,
                vehicleTypeId: task.vehicleTypeId ? Number(task.vehicleTypeId) : null,
                escortCount: task.escortCount?.toString() || '1',
                perVehicleCost: formatCentsToDecimalString(task.perVehicleCost),
                gpsTracking: task.gpsTracking || '',
                trailerNumber: task.trailerNumber || '',
                remark: task.remark || '',
                status: task.taskStatus as TmsTaskStatusEnum,
                isTransit: currentTransitInfos.length > 0 ? IS_TRANSIT.YES : IS_TRANSIT.NO, // 有转运记录则回填为”是”
                isBulkConsolidated: task.isBulkConsolidated ?? false,
            });
            // 回填转运行（按 tms_task_transit_info 已存数据）
            setTransitRows(
                currentTransitInfos.map((ti) => ({
                    key: `transit-${ti.id}`,
                    transitWarehouseId: ti.transitWarehouseId != null ? String(ti.transitWarehouseId) : '',
                    transitDestWarehouseId: ti.transitDestWarehouseId != null ? String(ti.transitDestWarehouseId) : '',
                    destLoaded: ti.destLoaded ?? false,
                    destUnloaded: ti.destUnloaded ?? false,
                }))
            );

            // 在查看/编辑任务详情时，直接使用任务中的 vehicleId（从 tms_task 表获取）
            // 不应该再查询司机账户绑定的车辆，因为任务可能手动选择了不同的车辆
            // 只有在创建新任务时，才应该根据司机账户绑定的车辆来自动填充
        }
    }, [task, taskDetailData, vehiclesData, driverOptions, driversData]);

    // 更新任务的 mutation
    const [updateTask, { loading: updating }] = useMutation<UpdateTmsTaskResponse>(UPDATE_TMS_TASK);
    const [saveTransitInfos] = useMutation<UpdateTmsTaskTransitInfosResponse>(UPDATE_TMS_TASK_TRANSIT_INFOS);
    const [saveBulkConsolidated] = useMutation<UpdateTmsTaskBulkConsolidatedResponse>(UPDATE_TMS_TASK_BULK_CONSOLIDATED);

    // “路线信息”区块共用的独立编辑入口：发布后(查看态)开放；Draft 走整体编辑（右下角确定），不显示此工具栏。
    // 区块内两个子项的可编辑范围不同——转运仍受原有限制（仅干线+自有路线、任务未完成）；
    // 散货拼车不受任务类型/状态限制，任意任务发布/完成后都靠这同一个入口编辑，不单独加按钮
    const [routeInfoEditing, setRouteInfoEditing] = useState(false);
    const showRouteInfoSectionToolbar = !isEditMode;
    // 转运子项本次是否真正可编辑（受原有业务限制）
    const canEditTransitInSection = showTransit && task?.taskStatus !== TmsTaskStatusEnum.Completed;

    // 取消/重置：转运行、散货拼车标记都回到服务端已存数据
    const resetTransitRowsFromServer = () => {
        setTransitRows(
            (taskDetailData?.transitInfos || []).map((ti) => ({
                key: `transit-${ti.id}`,
                transitWarehouseId: ti.transitWarehouseId != null ? String(ti.transitWarehouseId) : '',
                transitDestWarehouseId: ti.transitDestWarehouseId != null ? String(ti.transitDestWarehouseId) : '',
                destLoaded: ti.destLoaded ?? false,
                destUnloaded: ti.destUnloaded ?? false,
            }))
        );
        handleFilterChange('isTransit', (taskDetailData?.transitInfos?.length ?? 0) > 0 ? IS_TRANSIT.YES : IS_TRANSIT.NO);
    };

    const handleRouteInfoSectionCancel = () => {
        resetTransitRowsFromServer();
        setFilters(prev => ({ ...prev, isBulkConsolidated: task?.isBulkConsolidated ?? false }));
        setRouteInfoEditing(false);
    };

    const handleRouteInfoSectionSave = async () => {
        if (!taskId) return;
        if (canEditTransitInSection && !validateTransit()) return;
        try {
            if (canEditTransitInSection) {
                const transitInfos = getTransitInfosInput() ?? [];
                const res = (await saveTransitInfos({
                    variables: { input: { taskId: Number(taskId), transitInfos } },
                })).data?.updateTmsTaskTransitInfos;
                if (!res?.success) {
                    message.error(parseResultCode(res?.code ?? '', res?.message ?? undefined, parseResultArgs(res?.argsJson)) || String(t('tms.task.messages.failedToUpdateTask')));
                    return;
                }
            }
            const bulkRes = (await saveBulkConsolidated({
                variables: { input: { taskId: Number(taskId), isBulkConsolidated: filters.isBulkConsolidated } },
                refetchQueries: [{ query: GET_TMS_TASK_DETAIL, variables: { taskId: String(taskId) } }],
                awaitRefetchQueries: true,
            })).data?.updateTmsTaskBulkConsolidated;
            if (!bulkRes?.success) {
                message.error(parseResultCode(bulkRes?.code ?? '', bulkRes?.message ?? undefined, parseResultArgs(bulkRes?.argsJson)) || String(t('tms.task.messages.failedToUpdateTask')));
                return;
            }
            message.success(t('tms.task.messages.taskUpdatedSuccessfully'));
            setRouteInfoEditing(false);
        } catch (error: any) {
            const hasMessageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            hasMessageKey ? showError(error) : message.error(error?.message || t('tms.task.messages.failedToUpdateTask'));
        }
    };

    const handleClose = (skipFilterReset?: boolean) => {
        if (task && !skipFilterReset) {
            // 检查 driver1Id 是否在 driverOptions 中存在，如果不存在则设为空字符串
            let driver1Id = '';
            if (task.driver1Id) {
                const driverIdStr = task.driver1Id.toString();
                const driverExists = driverOptions.some(option => option.value === driverIdStr);
                driver1Id = driverExists ? driverIdStr : '';
            }
            // 司机2 同理：能匹配到系统司机就带 id，自由输入的只回显姓名
            let driver2Id = '';
            if (task.driver2Id) {
                const driver2IdStr = task.driver2Id.toString();
                driver2Id = driverOptions.some(option => option.value === driver2IdStr) ? driver2IdStr : '';
            }

            // 联系方式：优先取任务上的司机1电话，没有则从司机账号查
            let driver1PhoneNumber = task.driver1PhoneNumber || '';
            if (!driver1PhoneNumber && driver1Id && driversData?.getAccountTmsDriversByCarrierId) {
                const matchedDriver = driversData.getAccountTmsDriversByCarrierId.find(
                    d => d.id.toString() === driver1Id
                );
                if (matchedDriver?.phoneNumber) {
                    driver1PhoneNumber = matchedDriver.phoneNumber;
                }
            }
            
            setFilters({
                routeId: task.routeId?.toString() || '',
                taskDate: task.taskDate || '',
                originPlannedDepartureTime: task.planDepartureTime || '',
                taskType: task.taskType as TmsTaskTypeEnum,
                businessType: task.businessType?.toString() || '',
                driver1Id,
                driver1Name: task.driver1Name || '',
                driver1PhoneNumber,
                driver2Id,
                driver2Name: task.driver2Name || '',
                driver2PhoneNumber: task.driver2PhoneNumber || '',
                vehicleId: task.vehicleId?.toString() || '',
                licensePlate: task.licensePlate || '',
                vehicleModel: '',
                vehicleTypeId: task.vehicleTypeId ? Number(task.vehicleTypeId) : null,
                escortCount: task.escortCount?.toString() || '1',
                perVehicleCost: formatCentsToDecimalString(task.perVehicleCost),
                gpsTracking: task.gpsTracking || '',
                trailerNumber: task.trailerNumber || '',
                remark: task.remark || '',
                status: task.taskStatus as TmsTaskStatusEnum,
                isTransit: (taskDetailData?.transitInfos?.length ?? 0) > 0 ? IS_TRANSIT.YES : IS_TRANSIT.NO,
                isBulkConsolidated: task.isBulkConsolidated ?? false,
            });
            // 关闭时把转运行重置回服务端已存数据，避免下次打开残留未保存的编辑
            setTransitRows(
                (taskDetailData?.transitInfos || []).map((ti) => ({
                    key: `transit-${ti.id}`,
                    transitWarehouseId: ti.transitWarehouseId != null ? String(ti.transitWarehouseId) : '',
                    transitDestWarehouseId: ti.transitDestWarehouseId != null ? String(ti.transitDestWarehouseId) : '',
                    destLoaded: ti.destLoaded ?? false,
                    destUnloaded: ti.destUnloaded ?? false,
                }))
            );
        }
        setConfirmModalVisible(false);
        onClose();
    };

    // 交接类型编辑校验：无必填项
    const validateHandoverTask = (): boolean => {
        return true;
    };

    // 司机1/2 电话的异步重号检测结果（由 DriverPhoneRow 的 onPhoneBlur 触发，保持失焦即报错的交互）
    const [driver1PhoneError, setDriver1PhoneError] = useState<string | null>(null);
    const [driver2PhoneError, setDriver2PhoneError] = useState<string | null>(null);

    const runCheckAccountByPhone = async (phoneNumber: string) =>
        (await checkAccountByPhoneQuery({ variables: { phoneNumber } })).data?.checkAccountByPhone;

    /**
     * 是否要对这个号码做重号检测，司机1/2 同一套口径：非交接，且号码是用户新填或改动过的。
     * 三种「没动过」的号必须放过，否则打开详情就误报——号码本就属于当前这个司机：
     * 选中系统司机自动带出的（autoFilled）、与详情原值相同的。
     * 手输司机要建账号，即便号码没改也得查，否则会撞后端手机号唯一约束。
     */
    const needPhoneCheck = (phoneNumber: string, autoFilled: boolean, freeInput: boolean, originPhone?: string | null) =>
        !isHandoverRoute && !autoFilled && (freeInput || phoneNumber !== (originPhone || ''));

    /**
     * 司机1/2 电话失焦时做重号检测。
     * 返回错误文案而不只 setState：提交时读 state，闭包里还是旧值。
     */
    const validateDriver1PhoneAsync = async (phoneNumber: string): Promise<string | null> => {
        setDriver1PhoneError(null);
        const error = await checkDriverPhoneDuplicate({
            phoneNumber,
            enabled: needPhoneCheck(phoneNumber, isDriver1PhoneAutoFilled, isDriver1FreeInput, task?.driver1PhoneNumber),
            checkAccountByPhone: runCheckAccountByPhone,
            t: (key) => String(t(key)),
        });
        setDriver1PhoneError(error);
        return error;
    };

    const validateDriver2PhoneAsync = async (phoneNumber: string): Promise<string | null> => {
        setDriver2PhoneError(null);
        const error = await checkDriverPhoneDuplicate({
            phoneNumber,
            enabled: needPhoneCheck(phoneNumber, isDriver2PhoneAutoFilled, isDriver2FreeInput, task?.driver2PhoneNumber),
            conflictPhoneNumber: filters.driver1PhoneNumber,
            checkAccountByPhone: runCheckAccountByPhone,
            t: (key) => String(t(key)),
        });
        setDriver2PhoneError(error);
        return error;
    };

    // 司机2 非必填，但填了就必须是 10 位；交接与非交接类型都要校验，故独立成一个方法。
    // 跟车人数<=1 时司机2 不展示，跳过司机2 自身的两项校验，但下面司机1 的校验仍要执行
    const validateDriver2Phone = (): boolean => {
        if (showDriver2) {
            if (isPhoneInvalid(filters.driver2PhoneNumber)) {
                message.error(t('tms.task.messages.pleaseInputValidPhoneNumber'));
                return false;
            }
            if (driver2PhoneError) {
                message.error(driver2PhoneError);
                return false;
            }
        }
        // 司机1：格式与重号检测都要拦在提交前，否则失焦报了错还能保存
        if (isPhoneInvalid(filters.driver1PhoneNumber)) {
            message.error(t('tms.task.messages.pleaseInputValidPhoneNumber'));
            return false;
        }
        if (driver1PhoneError) {
            message.error(driver1PhoneError);
            return false;
        }
        return true;
    };

    // 非交接类型编辑校验：司机、联系方式、跟车人数、单车成本、柜号、车辆、车型必填
    const validateRegularTask = (): boolean => {
        if (!filters.driver1Name) {
            message.error(t('tms.task.messages.pleaseSelectDriver1'));
            return false;
        }
        // 未填就直说填哪个字段；格式不对才用「请填写正确合规手机号」
        if (!filters.driver1PhoneNumber) {
            message.error(t('tms.task.messages.pleaseInputDriver1Phone'));
            return false;
        }
        if (!filters.escortCount?.trim()) {
            message.error(t('tms.task.messages.pleaseInputEscortCount'));
            return false;
        }
        if (shouldIncludePerVehicleCost) {
            if (!filters.perVehicleCost?.trim()) {
                message.error(t('tms.task.messages.pleaseInputPerVehicleCost'));
                return false;
            }
            if (parseDecimalStringToCents(filters.perVehicleCost) < 0) {
                message.error(t('tms.task.messages.perVehicleCostCannotBeNegative'));
                return false;
            }
        }
        if (!filters.trailerNumber?.trim()) {
            message.error(t('tms.task.messages.pleaseInputTrailerNumber'));
            return false;
        }
        if (!isVehicleFreeInput && !filters.vehicleId) {
            message.error(t('tms.task.messages.pleaseSelectVehicle'));
            return false;
        }
        if (!filters.vehicleModel) {
            message.error(t('tms.task.messages.pleaseSelectVehicleModel'));
            return false;
        }
        return true;
    };

    // 转运校验：选「是」时，每行是「转运目的地站 + 转运站」配对。空行忽略；半填行(只填一列)报错需成对；至少要有一组完整配对
    const validateTransit = (): boolean => {
        if (!showTransit || filters.isTransit !== IS_TRANSIT.YES) return true;
        // 半填(只填了一列)= 不完整配对
        const hasPartial = transitRows.some((r) => !!r.transitDestWarehouseId !== !!r.transitWarehouseId);
        if (hasPartial) {
            message.error(t('tms.task.drawer.transitTable.incompletePair'));
            return false;
        }
        // 至少一组完整配对
        const hasComplete = transitRows.some((r) => r.transitDestWarehouseId && r.transitWarehouseId);
        if (!hasComplete) {
            message.error(t('tms.task.drawer.transitTable.required'));
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!taskId || !task) return;

        // 根据路线类型分发校验
        const isValid = isHandoverRoute
            ? validateHandoverTask()
            : validateRegularTask();
        if (!isValid) return;

        // 转运校验：填了转运目的地站则转运站必填
        if (!validateTransit()) return;

        // 联系方式格式校验：必填由 validateRegularTask 判断，这里只管填了就得是 10 位
        if (isPhoneInvalid(filters.driver1PhoneNumber)) {
            message.error(t('tms.task.messages.pleaseInputValidPhoneNumber'));
            return;
        }
        // 重号检测：司机1、司机2 同一套口径，是否要检测由各自的 validate 方法内部判断
        const phoneError = await validateDriver1PhoneAsync(filters.driver1PhoneNumber);
        if (phoneError) {
            message.error(phoneError);
            return;
        }
        if (showDriver2) {
            const driver2Error = await validateDriver2PhoneAsync(filters.driver2PhoneNumber);
            if (driver2Error) {
                message.error(driver2Error);
                return;
            }
        }

        // 非交接类型，自由输入司机1/司机2/车辆时弹出确认弹窗
        if (!isHandoverRoute && (isDriver1FreeInput || isDriver2FreeInput || isVehicleFreeInput)) {
            // 自由编辑车辆时：弹窗前校验车牌是否已存在且绑定其他承运商
            if (isVehicleFreeInput && task?.carrierId != null) {
                const licensePlate = (filters.licensePlate || '').trim();
                if (licensePlate) {
                    try {
                        const { data } = await getVehiclesByLicensePlate({
                            variables: { licensePlate }
                        });
                        const list = data?.allVehicles ?? [];
                        const currentCarrierId = Number(task.carrierId);
                        const boundToOther = list.find((v: { carrierId: number; licensePlate?: string; vehicleNumber?: string; carrierName?: string }) =>
                            (v.licensePlate === licensePlate || v.vehicleNumber === licensePlate) &&
                            v.carrierId !== currentCarrierId
                        );
                        if (boundToOther) {
                            Modal.error({
                                title: t('tms.task.messages.vehicleBoundToOtherCarrierTitle'),
                                content: t('tms.task.messages.vehicleBoundToOtherCarrierContent', {
                                    licensePlate: boundToOther.licensePlate || boundToOther.vehicleNumber || licensePlate,
                                    carrierName: boundToOther.carrierName || ''
                                })
                            });
                            return;
                        }
                    } catch (e) {
                        console.error('Check vehicle by license plate failed:', e);
                    }
                }
            }
            // 只在弹窗未打开时重置，避免覆盖用户已修改的值
            if (isDriver1FreeInput && !confirmModalVisible) {
                driver1Form.resetFields();
                driver1Form.setFieldsValue({
                    name: filters.driver1Name,
                    phone: { countryCode: 1, phoneNumber: filters.driver1PhoneNumber || '' },
                    role: 'TMS_DRIVER',
                    status: 1,
                    ...(isVehicleFreeInput
                        ? { vehicleDisplay: filters.licensePlate || '' }
                        : { vehicleId: filters.vehicleId ? Number(filters.vehicleId) : undefined })
                });
            }
            // 司机2 弹窗表单：字段同司机1，但不绑定车辆，故无车辆字段
            if (isDriver2FreeInput && !confirmModalVisible) {
                driver2Form.resetFields();
                driver2Form.setFieldsValue({
                    name: filters.driver2Name,
                    phone: { countryCode: 1, phoneNumber: filters.driver2PhoneNumber || '' },
                    role: 'TMS_DRIVER',
                    status: 1
                });
            }
            if (isVehicleFreeInput && !confirmModalVisible) {
                vehicleForm.resetFields();
                const matchedModel = vehicleModelOptions.find(o => o.label === filters.vehicleModel);
                vehicleForm.setFieldsValue({
                    licensePlate: filters.licensePlate,
                    status: 1,
                    vehicleTypeId: matchedModel?.value
                });
                if (matchedModel?.value) handleVehicleTypeChange(matchedModel.value);
            }
            setConfirmModalVisible(true);
            return;
        }

        // 非交接类型，从列表选择时：必填司机、车辆、车型
        if (!isHandoverRoute) {
            if (!filters.driver1Id) {
                message.error(t('tms.task.messages.pleaseSelectDriver1'));
                return;
            }
            if (!filters.vehicleId) {
                message.error(t('tms.task.messages.pleaseSelectVehicle'));
                return;
            }
            if (!filters.vehicleModel) {
                message.error(t('tms.task.messages.pleaseSelectVehicleModel'));
                return;
            }
        }

        if (!validateDriver2Phone()) return;

        try {
            const input: UpdateTmsTaskInput = {
                driver1Id: filters.driver1Id ? parseInt(filters.driver1Id) : null,
                driver1Name: filters.driver1Name || null,
                driver1PhoneNumber: filters.driver1PhoneNumber || null,
                // 司机2 必须一并回传：后端全量覆盖，不传会把已有司机2 清空；跟车人数<=1 时不展示司机2，强制置空
                driver2Id: showDriver2 && filters.driver2Id ? parseInt(filters.driver2Id) : null,
                driver2Name: showDriver2 ? (filters.driver2Name || null) : null,
                driver2PhoneNumber: showDriver2 ? (filters.driver2PhoneNumber || null) : null,
                vehicleId: filters.vehicleId ? parseInt(filters.vehicleId) : null,
                trailerNumber: filters.trailerNumber || null,
                remark: filters.remark || null,
                escortCount: Number.parseInt(filters.escortCount || '0', 10),
                perVehicleCost: shouldIncludePerVehicleCost ? parseDecimalStringToCents(filters.perVehicleCost) : undefined,
                gpsTracking: filters.gpsTracking || null,
                licensePlate: filters.licensePlate || null,
                vehicleTypeId: filters.vehicleTypeId,
                // 回传交接方类型，供后端判断本地尾程商交接任务可在任意状态编辑
                carrierSourceType: task?.carrierSourceType ?? null,
                transitInfos: getTransitInfosInput(),
                isBulkConsolidated: filters.isBulkConsolidated,
            };

            const res = (await updateTask({ variables: { id: taskId, input } })).data?.updateTmsTask;
            if (!res?.success) {
                message.error(parseResultCode(res?.code ?? '', res?.message ?? undefined, parseResultArgs(res?.argsJson)) || String(t('tms.task.messages.failedToUpdateTask')));
                return;
            }

            message.success(t('tms.task.messages.taskUpdatedSuccessfully'));
            onSubmit?.();
            handleClose(true); // 保存成功后跳过 filters 重置，避免用缓存的旧 task 覆盖导致输入框闪烁
        } catch (error: any) {
            console.error('Failed to update task:', error);
            const hasMessageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            hasMessageKey ? showError(error) : message.error(error?.message || t('tms.task.messages.failedToUpdateTask'));
        }
    };

    // 装车明细和卸车明细Drawer状态
    const [loadingDetailDrawer, setLoadingDetailDrawer] = useState<{ open: boolean; waypointId: number }>({ open: false, waypointId: 0 });
    const [unloadingDetailDrawer, setUnloadingDetailDrawer] = useState<{ open: boolean; waypointId: number }>({ open: false, waypointId: 0 });

    // 获取waypoint和sortingCode的辅助函数
    const getWaypointInfo = (taskWaypointId: number) => {
        const currentWaypoint = taskWaypointId ? waypoints.find(wp => wp.id === taskWaypointId.toString()) : null;
        const sortingCode = currentWaypoint?.waypointSortingCode || undefined;
        return { currentWaypoint, sortingCode };
    };

    // 根据异常原因ID获取图标和名称
    const getExceptionReasonInfo = (exceptionReasonId: number): { icon: React.ReactNode; name: string } => {
        const iconClassName = "text-red-500 text-xl mr-2";
        const reasonMap: Record<ExceptionReasonEnum, { icon: React.ReactNode; name: string }> = {
            [ExceptionReasonEnum.VEHICLE_BREAKDOWN]: { icon: <ToolOutlined className={iconClassName} />, name: String(t('tms.task.exceptionReasons.vehicleBreakdown')) },
            [ExceptionReasonEnum.TRAFFIC_ACCIDENT]: { icon: <ExclamationCircleOutlined className={iconClassName} />, name: String(t('tms.task.exceptionReasons.trafficAccident')) },
            [ExceptionReasonEnum.WEATHER]: { icon: <ThunderboltOutlined className={iconClassName} />, name: String(t('tms.task.exceptionReasons.weather')) },
            [ExceptionReasonEnum.TRAFFIC_JAM]: { icon: <TruckOutlined className={iconClassName} />, name: String(t('tms.task.exceptionReasons.trafficJam')) },
            [ExceptionReasonEnum.WAREHOUSE_DELAY]: { icon: <HomeOutlined className={iconClassName} />, name: String(t('tms.task.exceptionReasons.warehouseDelay')) },
            [ExceptionReasonEnum.OTHER]: { icon: <ProductOutlined className={iconClassName} />, name: String(t('tms.task.exceptionReasons.other')) },
        };
        return reasonMap[exceptionReasonId as ExceptionReasonEnum]
    };

    const parseExceptionPhotos = (photosUrls: string[]): string[] => {
        // exceptionPhotosUrlsJson 已经是 URL 列表，直接返回
        return Array.isArray(photosUrls) ? photosUrls : [];
    };

    // 承运商信息区是一行三格，两个司机各占一行「姓名 + 电话」、第三格留空；
    // GPS追踪号与柜号的落位随业务类型变：
    // - 非交接：车辆/车型一行，柜号 + GPS 另起一行
    // - 交接：不展示单车成本，GPS 提到第一行补空位，柜号跟车辆/车型同一行
    const gpsTrackingCell = (
        <div>
            <InputFilterItem
                label={String(t('tms.task.drawer.gpsTracking'))}
                placeholder={isEditMode ? String(t('tms.task.drawer.placeholderGpsTracking')) : ''}
                value={filters.gpsTracking || (!isEditMode ? '-' : '')}
                onChange={(e) => handleFilterChange('gpsTracking', e.target.value)}
                spanClassName="w-[7em]"
                forceHorizontal
                disabled={!isEditMode}
                maxLength={999}
                showCount={isEditMode}
            />
        </div>
    );

    const trailerNumberCell = (
        <div>
            <InputFilterItem
                label={String(t('tms.task.bol.trailerNumber'))}
                placeholder={isEditMode ? String(t('tms.task.bol.placeholderTrailerNumber')) : ''}
                value={filters.trailerNumber || (!isEditMode ? '-' : '')}
                onChange={(e) => handleFilterChange('trailerNumber', e.target.value)}
                spanClassName="w-[7em]"
                disabled={!isEditMode}
                forceHorizontal
                required={!isHandoverRoute}
                maxLength={50}
                showCount={isEditMode}
            />
        </div>
    );

    return (
        <>
            <EditableDrawer
                title={drawerTitle}
                visible={open}
                onClose={handleClose}
                onSubmit={isEditMode ? handleSubmit : undefined}
                confirmDisabled={updating}
                confirmLoading={updating}
                width={1200}
                showDefaultFooter={isEditMode}
                destroyOnClose
                zIndex={1000}
            >
                {/* 上部分：筛选框 */}
                <div className="bg-white mb-4">
                    <div className="relative mt-2 mb-4 px-1">
                        <span className="text-black font-bold text-base">{String(t('tms.task.drawer.routeInfo'))}</span>
                        {/* 路线信息区块编辑：发布后(查看态)开放；其余字段本就只读。区块内散货拼车任意任务/状态都可改，
                            转运仍受"仅干线+自有路线、任务未完成"限制（见 canEditTransitInSection） */}
                        {showRouteInfoSectionToolbar && (
                            <EditToolbar
                                isEditing={routeInfoEditing}
                                allowEdit={true}
                                onStartEdit={() => setRouteInfoEditing(true)}
                                onSave={handleRouteInfoSectionSave}
                                onCancel={handleRouteInfoSectionCancel}
                            />
                        )}
                    </div>
                    <div className="grid grid-cols-3 gap-4 px-4 mb-4">
                        <div>
                            <InputFilterItem
                                label={String(t('tms.task.drawer.route'))}
                                value={task?.routeName || '-'}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                            />
                        </div>
                        <div>
                            <InputFilterItem
                                label={String(t('tms.task.drawer.taskDate'))}
                                value={filters.taskDate}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                            />
                        </div>
                        <div>
                            <InputFilterItem
                                label={String(t('tms.task.drawer.departureTime'))}
                                value={filters.originPlannedDepartureTime || '-'}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                            />
                        </div>
                        <div>
                            <InputFilterItem
                                label={String(t('tms.task.drawer.taskType'))}
                                value={(() => {
                                    const labelKey = getTaskTypeLabelKey(filters.taskType);
                                    return labelKey ? String(t(labelKey)) : '-';
                                })()}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                            />
                        </div>
                        <div>
                            <SingleSelect
                                label={String(t('tms.task.drawer.isBulkConsolidated'))}
                                options={[
                                    { value: 'true', label: String(t('common.yes')) },
                                    { value: 'false', label: String(t('common.no')) },
                                ]}
                                value={String(filters.isBulkConsolidated)}
                                onChange={(value) => setFilters(prev => ({ ...prev, isBulkConsolidated: value === 'true' }))}
                                spanClassName="w-[7em]"
                                required
                                allowClear={false}
                                disabled={!(isEditMode || routeInfoEditing)}
                                forceHorizontal
                            />
                        </div>
                        {/* 转运信息：仅干线+自有路线任务展示，第三行起；转运子项是否可编辑仍受 canEditTransitInSection 限制 */}
                        {showTransit && (
                            <TransitSection
                                isTransit={filters.isTransit}
                                onIsTransitChange={(value) => handleFilterChange('isTransit', value)}
                                rows={transitRows}
                                onRowsChange={setTransitRows}
                                disabled={!(isEditMode || (routeInfoEditing && canEditTransitInSection))}
                                transitStationOptions={transitStationOptions}
                                transitDestStationOptions={transitDestStationOptions}
                            />
                        )}
                    </div>
                    <div className="text-black font-bold text-base mt-4 mb-4 px-1">
                        {String(t(isHandoverRoute ? 'tms.task.drawer.handoverCarrierInfo' : 'tms.task.drawer.carrierInfo'))}
                    </div>
                    <div className="grid grid-cols-3 gap-4 px-4 mb-8">
                        <div>
                            <InputFilterItem
                                label={String(t(isHandoverRoute ? 'tms.task.drawer.handoverCarrier' : 'tms.task.drawer.carrier'))}
                                value={task?.carrierName || '-'}
                                spanClassName="w-[7em]"
                                disabled
                                forceHorizontal
                                required
                            />
                        </div>
                        <div>
                            {isEditMode ? (
                                <SingleSelect
                                    label={String(t('tms.task.drawer.escortCount'))}
                                    options={escortCountOptions}
                                    value={filters.escortCount}
                                    onChange={(value) => {
                                        const nextValue = value ?? '';
                                        handleFilterChange('escortCount', nextValue);
                                        // 跟车人数改回1：清空司机2，避免隐藏后残留数据被提交
                                        if (Number(nextValue) <= 1) {
                                            setFilters(prev => ({ ...prev, driver2Id: '', driver2Name: '', driver2PhoneNumber: '' }));
                                            setIsDriver2PhoneAutoFilled(false);
                                            setDriver2TypedByUser(false);
                                            setDriver2PhoneError(null);
                                        }
                                    }}
                                    spanClassName="w-[7em]"
                                    forceHorizontal
                                    required
                                    disabled={!isEditMode}
                                    allowClear={false}
                                />
                            ) : (
                                <InputFilterItem
                                    label={String(t('tms.task.drawer.escortCount'))}
                                    value={filters.escortCount}
                                    spanClassName="w-[7em]"
                                    forceHorizontal
                                    disabled
                                />
                            )}
                        </div>
                        {shouldIncludePerVehicleCost && (
                            <div>
                                <InputFilterItem
                                    label={String(t('tms.task.drawer.perVehicleCost'))}
                                    value={filters.perVehicleCost}
                                    onChange={(e) => handleFilterChange('perVehicleCost', sanitizeDecimalInput(e.target.value))}
                                    spanClassName="w-[7em]"
                                    forceHorizontal
                                    required
                                    disabled={!isEditMode}
                                />
                            </div>
                        )}
                        {isHandoverRoute && gpsTrackingCell}
                        {/* 司机1：姓名与电话各占一格（本区块统一「标签: 控件」一行三格）；组件在拆分模式下直接返回两个栅格单元 */}
                        <DriverPhoneRow
                            labels={{
                                name: String(t('tms.task.drawer.driver1')),
                                phone: String(t('tms.task.drawer.driver1Phone')),
                            }}
                            required={!isHandoverRoute}
                            freeInputOnly={isHandoverRoute}
                            driverOptions={driver1Options}
                            driverPhoneById={driverPhoneById}
                            value={{
                                driverId: filters.driver1Id,
                                driverName: filters.driver1Name,
                                phoneNumber: filters.driver1PhoneNumber,
                            }}
                            onChange={(v) => setFilters(prev => ({
                                ...prev,
                                driver1Id: v.driverId,
                                driver1Name: v.driverName,
                                driver1PhoneNumber: v.phoneNumber,
                            }))}
                            disabled={!isEditMode}
                            namePlaceholder={String(t(isHandoverRoute ? 'tms.task.drawer.inputDriver1' : 'tms.task.drawer.selectDriver1'))}
                            phonePlaceholder={String(t('tms.task.drawer.driver1PhonePlaceholder'))}
                            phoneDisabled={isDriver1PhoneAutoFilled}
                            phoneError={driver1PhoneError}
                            onPhoneFocus={() => setDriver1PhoneError(null)}
                            onPhoneBlur={validateDriver1PhoneAsync}
                            containerClassName={containerClassName}
                            labelClassName={labelClassName}
                            inputContainerClassName={inputContainerClassName}
                            onDriverSelected={(driverId, _name, phoneNumber) => {
                                if (isHandoverRoute) return; // 交接类型无下拉，不会触发
                                // 换司机即清空车辆/车牌/车型，由所选司机重新带出
                                setFilters(prev => ({
                                    ...prev,
                                    vehicleId: '',
                                    licensePlate: '',
                                    vehicleModel: '',
                                    vehicleTypeId: null,
                                }));
                                setIsVehicleFreeInput(false);
                                setIsDriver1FreeInput(false);
                                setIsDriver1PhoneAutoFilled(!!phoneNumber);
                                setDriver1PhoneError(null);
                                getVehicleAndModel({ variables: { driverId: parseInt(driverId) } });
                            }}
                            onDriverCleared={() => {
                                if (isHandoverRoute) return; // 交接类型：司机与其他字段无关联，不清空
                                setFilters(prev => ({
                                    ...prev,
                                    vehicleId: '',
                                    licensePlate: '',
                                    vehicleModel: '',
                                    vehicleTypeId: null,
                                    driver1PhoneNumber: '',
                                }));
                                setIsVehicleAutoSet(false);
                                setIsVehicleFreeInput(false);
                                setDriver1BoundVehicle(null);
                                setIsDriver1FreeInput(true);
                                setIsDriver1PhoneAutoFilled(false);
                                setDriver1PhoneError(null);
                            }}
                        />
                        {/* 司机1 一行只放「姓名 + 电话」，第三格留空 */}
                        <div />
                        {/* 司机2：跟车人数>1才展示；非必填，仅BOL展示，不参与派单/APP/结算；可选系统司机，也可自由输入姓名 */}
                        {showDriver2 && (
                        <DriverPhoneRow
                            labels={{
                                name: String(t('tms.task.drawer.driver2')),
                                phone: String(t('tms.task.drawer.driver2Phone')),
                            }}
                            driverOptions={driver2Options}
                            driverPhoneById={driverPhoneById}
                            value={{
                                driverId: filters.driver2Id,
                                driverName: filters.driver2Name,
                                phoneNumber: filters.driver2PhoneNumber,
                            }}
                            onChange={(v) => {
                                // 司机2 从「所选系统司机」改成手输：该司机带出的电话要跟着清掉（手输的电话保留）。
                                // 只动电话——司机2 不关联车辆/车型，不像司机1 那样联动清空
                                const droppedSystemDriver = !v.driverId && !!filters.driver2Id;
                                if (v.driverId || droppedSystemDriver) setDriver2PhoneError(null);
                                // 改成手输即解灰；置灰只在 onDriverSelected 里判一次——放在这里会导致
                                // 「已选系统司机但电话为空」时，用户手输第一个数字就被置灰
                                if (droppedSystemDriver) setIsDriver2PhoneAutoFilled(false);
                                setFilters(prev => ({
                                    ...prev,
                                    driver2Id: v.driverId,
                                    driver2Name: v.driverName,
                                    driver2PhoneNumber: droppedSystemDriver ? '' : v.phoneNumber,
                                }));
                            }}
                            disabled={!isEditMode}
                            freeInputOnly={isHandoverRoute}
                            // 选中系统司机且带出了电话才置灰（与司机1 同口径）
                            onDriverSelected={(_driverId, _name, phoneNumber) => {
                                setDriver2TypedByUser(false);
                                setIsDriver2PhoneAutoFilled(!!phoneNumber);
                            }}
                            // 手打姓名即视为手输司机2（交接类型 freeInputOnly，组件不会触发此回调）
                            onDriverCleared={() => {
                                setDriver2TypedByUser(true);
                                setIsDriver2PhoneAutoFilled(false);
                                setDriver2PhoneError(null);
                            }}
                            namePlaceholder={String(t(isHandoverRoute ? 'tms.task.drawer.inputDriver2' : 'tms.task.drawer.selectDriver2'))}
                            phonePlaceholder={String(t('tms.task.drawer.driver2PhonePlaceholder'))}
                            phoneDisabled={isDriver2PhoneAutoFilled}
                            phoneError={driver2PhoneError}
                            onPhoneFocus={() => setDriver2PhoneError(null)}
                            onPhoneBlur={validateDriver2PhoneAsync}
                            containerClassName={containerClassName}
                            labelClassName={labelClassName}
                            inputContainerClassName={inputContainerClassName}
                        />
                        )}
                        {/* 司机2 同样只占两格，第三格留空；随司机2 一起隐藏/展示，保持3列栅格整行对齐 */}
                        {showDriver2 && <div />}
                        <div>
                            {isEditMode ? (
                                <div className={containerClassName}>
                                    <span className={labelClassName}>
                                        {String(t('tms.task.drawer.vehicle'))}
                                        {!isHandoverRoute && <span className="text-red-500 ml-1">*</span>}
                                    </span>
                                    <div className={inputContainerClassName}>
                                        <AutoComplete
                                            className="w-full"
                                            placeholder={String(t(isHandoverRoute ? 'tms.task.drawer.inputLicensePlate' : 'tms.task.drawer.selectVehicle'))}
                                            options={vehicleOptions}
                                            value={filters.licensePlate}
                                            onChange={(value) => {
                                                const matchedVehicleOption = vehicleOptions.find(opt => opt.label === value);
                                                if (value && !matchedVehicleOption) {
                                                    // 输入的车辆不在列表中（自由输入），只清空 vehicleId
                                                    setFilters(prev => ({ ...prev, licensePlate: value, vehicleId: '' }));
                                                    setIsVehicleFreeInput(true);
                                                } else if (!value) {
                                                    // 清空输入
                                                    setFilters(prev => ({ ...prev, licensePlate: '', vehicleId: '', vehicleModel: '', vehicleTypeId: null }));
                                                    setIsVehicleFreeInput(false);
                                                } else if (matchedVehicleOption) {
                                                    // 输入内容与下拉列表中的某一项完全匹配时，自动选择该车辆
                                                    const valueAsString = String(matchedVehicleOption.value);
                                                    const matchedVehicle = vehiclesData?.getTmsVehiclesWithModel?.find(
                                                        v => v.id.toString() === valueAsString
                                                    );
                                                    setFilters(prev => ({
                                                        ...prev,
                                                        vehicleId: valueAsString,
                                                        licensePlate: matchedVehicleOption.label as string,
                                                        vehicleModel: matchedVehicle?.vehicleTypeName || prev.vehicleModel,
                                                        vehicleTypeId: matchedVehicle?.vehicleTypeId || prev.vehicleTypeId,
                                                    }));
                                                    setIsVehicleFreeInput(false);
                                                }
                                            }}
                                            onSelect={(value, option) => {
                                                const matchedVehicle = vehiclesData?.getTmsVehiclesWithModel?.find(
                                                    v => v.id.toString() === value.toString()
                                                );
                                                setFilters(prev => ({
                                                    ...prev,
                                                    vehicleId: value as string,
                                                    licensePlate: option.label as string,
                                                    vehicleModel: matchedVehicle?.vehicleTypeName || prev.vehicleModel,
                                                    vehicleTypeId: matchedVehicle?.vehicleTypeId || prev.vehicleTypeId,
                                                }));
                                                setIsVehicleFreeInput(false);
                                            }}
                                            filterOption={(inputValue, option) =>
                                                (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase())
                                            }
                                        />
                                    </div>
                                </div>
                            ) : (
                                <InputFilterItem
                                    label={String(t('tms.task.drawer.vehicle'))}
                                    value={task?.licensePlate || '-'}
                                    spanClassName="w-[7em]"
                                    disabled
                                    forceHorizontal
                                    required={!isHandoverRoute}
                                />
                            )}
                        </div>
                        <div>
                            {isEditMode ? (
                                <SingleSelect
                                    label={String(t('tms.task.drawer.vehicleModel'))}
                                    placeholder={String(t((isHandoverRoute || isVehicleFreeInput) ? 'tms.task.drawer.selectVehicleModel' : 'tms.task.drawer.autoFilledFromVehicle'))}
                                    options={vehicleModelOptions}
                                    value={filters.vehicleTypeId ? String(filters.vehicleTypeId) : undefined}
                                    onChange={(value) => {
                                        const matched = vehicleModelOptions.find(o => String(o.value) === value);
                                        setFilters(prev => ({ ...prev, vehicleTypeId: value ? Number(value) : null, vehicleModel: matched?.label || '' }));
                                    }}
                                    spanClassName="w-[7em]"
                                    forceHorizontal
                                    disabled={!isHandoverRoute && !isVehicleFreeInput}
                                    required={!isHandoverRoute}
                                />
                            ) : (
                                <InputFilterItem
                                    label={String(t('tms.task.drawer.vehicleModel'))}
                                    placeholder={String(t('tms.task.drawer.autoFilledFromVehicle'))}
                                    value={filters.vehicleModel || '-'}
                                    spanClassName="w-[7em]"
                                    disabled
                                    forceHorizontal
                                    required={!isHandoverRoute}
                                />
                            )}
                        </div>
                        {isHandoverRoute ? (
                            /* 交接：柜号跟车辆、车型同一行（GPS 已排在第一行） */
                            trailerNumberCell
                        ) : (
                            /* 非交接：车辆/车型这行第三格留空，柜号与 GPS 追踪号另起一行 */
                            <>
                                <div />
                                {trailerNumberCell}
                                {gpsTrackingCell}
                            </>
                        )}
                    </div>
                    <div className="text-black font-bold text-base mt-4 mb-4 px-1">
                        {String(t('tms.task.drawer.remarkInfo'))}
                    </div>
                    <div className="grid grid-cols-3 gap-4 px-4 mb-8">
                        <div className="col-span-3">
                            <InputFilterItem
                                label={String(t('tms.task.drawer.remarkLabel'))}
                                placeholder={isEditMode ? String(t('tms.task.drawer.placeholderRemark')) : ''}
                                value={filters.remark || (!isEditMode ? '-' : '')}
                                onChange={(e) => handleFilterChange('remark', e.target.value)}
                                spanClassName="w-[7em]"
                                className="w-full"
                                maxLength={100}
                                showCount={isEditMode}
                                forceHorizontal
                                disabled={!isEditMode}
                            />
                        </div>
                    </div>
                    {/* 取消原因：仅已取消任务有值，只读展示；独立分组，与「备注」同级（标题 + 左标签右输入框） */}
                    {task?.cancelReason && (
                        <>
                            <div className="text-black font-bold text-base mt-4 mb-4 px-1">
                                {t('tms.task.drawer.cancelReasonLabel')}
                            </div>
                            <div className="grid grid-cols-3 gap-4 px-4 mb-8">
                                <div className="col-span-3">
                                    <InputFilterItem
                                        label={t('tms.task.drawer.cancelReasonLabel')}
                                        value={task.cancelReason}
                                        spanClassName="w-[7em]"
                                        className="w-full"
                                        forceHorizontal
                                        disabled
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* 中间部分：任务途经点详情 */}
                <TaskWaypointDetail
                    waypoints={waypoints}
                    onOpenLoadingDetailDrawer={(waypointId) => setLoadingDetailDrawer({ open: true, waypointId })}
                    onOpenUnloadingDetailDrawer={(waypointId) => setUnloadingDetailDrawer({ open: true, waypointId })}
                    task={task ?? undefined}
                />

                {/* 下部分：异常记录详情（仅在查看模式显示） */}
                {!isEditMode && (
                    <div className="bg-white mb-4">
                        {exceptions.length === 0 ? (
                            <div
                                className="text-gray-500 text-left pb-4">{String(t('tms.task.drawer.noExceptionRecords'))}</div>
                        ) : (
                            <div className="space-y-4">
                                {exceptions.map((exception) => {
                                    const photos = parseExceptionPhotos(exception.exceptionPhotosUrlsJson);
                                    const exceptionTime = exception.createdAt ? dayjs(exception.createdAt).format('YYYY-MM-DD HH:mm') : '-';
                                    const reasonInfo = getExceptionReasonInfo(exception.exceptionReasonType);

                                    return (
                                        <div key={exception.id} className="border border-gray-200 rounded p-4">
                                            {/* 第一行：异常原因 */}
                                            <div className="mb-2 flex items-center">
                                                {reasonInfo.icon}
                                                <span className="text-red-500 text-base font-medium">{reasonInfo.name}</span>
                                            </div>
                                            {/* 第二行：时间、地点 */}
                                            <div className="mb-2">
                                                <span>{String(t('tms.task.drawer.time'))} </span>
                                                <span className='font-bold'>{exceptionTime}</span>
                                                <span className="ml-6">{String(t('tms.task.drawer.location'))} </span>
                                                <span className='font-bold'>{exception.location}</span>
                                            </div>
                                            {/* 第三行：备注（如果有） */}
                                            {exception.remark && (
                                                <div className="mb-2">
                                                    <span className="font-medium">{String(t('tms.task.drawer.remark'))} </span>
                                                    <span className='font-bold'>{exception.remark}</span>
                                                </div>
                                            )}
                                            {/* 第四行：异常照片 */}
                                            {photos.length > 0 && (
                                                <div>
                                                    <div className="flex gap-2 flex-wrap">
                                                        {photos.map((photo, photoIndex) => (
                                                            <Image
                                                                key={photoIndex}
                                                                src={photo}
                                                                alt={`Exception photo ${photoIndex + 1}`}
                                                                width={100}
                                                                height={100}
                                                                className="object-cover"
                                                                preview
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </EditableDrawer>

            <TmsTaskConfirmDriverVehicleModal
                open={confirmModalVisible}
                onOk={handleConfirmSubmit}
                onCancel={() => setConfirmModalVisible(false)}
                title={getConfirmModalTitle()}
                confirmLoading={confirmSubmitting}
                isDriver1FreeInput={isDriver1FreeInput}
                isDriver2FreeInput={isDriver2FreeInput}
                isVehicleFreeInput={isVehicleFreeInput}
                driver1Form={driver1Form}
                driver2Form={driver2Form}
                vehicleForm={vehicleForm}
                carrierName={carrierName}
                driverVehicleOptions={confirmDriverVehicleOptions}
                driverVehicleLoading={driverVehicleLoading}
                vehicleModelOptions={vehicleModelOptions}
                fuelTypeOptions={fuelTypeOptions}
                statusOptions={statusOptions}
                onVehicleModelChange={handleVehicleTypeChange}
                zIndex={1100}
            />

            {/* 装车明细Drawer */}
            {(() => {
                const { currentWaypoint, sortingCode } = getWaypointInfo(loadingDetailDrawer.waypointId);
                return (
                    <LoadingDetailDrawer
                        open={loadingDetailDrawer.open}
                        onClose={() => setLoadingDetailDrawer({ open: false, waypointId: 0 })}
                        waypointId={loadingDetailDrawer.waypointId}
                        taskId={taskId ? parseInt(taskId) : undefined}
                        taskNumber={drawerTitle}
                        sortingCode={sortingCode}
                        loadingTime={currentWaypoint?.loadingTime || undefined}
                        routeName={task?.routeName || '-'}
                        taskWaypoints={waypoints}
                        waypointIdToSortingCodeMap={waypointIdToSortingCodeMap}
                    />
                );
            })()}

            {/* 卸车明细Drawer */}
            {(() => {
                const { currentWaypoint, sortingCode } = getWaypointInfo(unloadingDetailDrawer.waypointId);
                return (
                    <UnloadingDetailDrawer
                        open={unloadingDetailDrawer.open}
                        onClose={() => setUnloadingDetailDrawer({ open: false, waypointId: 0 })}
                        waypointId={unloadingDetailDrawer.waypointId}
                        taskId={taskId ? parseInt(taskId) : undefined}
                        taskNumber={drawerTitle}
                        sortingCode={sortingCode}
                        unSealTime={currentWaypoint?.unSealTime || undefined}
                        routeName={task?.routeName || '-'}
                        taskWaypoints={waypoints}
                        waypointIdToSortingCodeMap={waypointIdToSortingCodeMap}
                        isLinehaulUnload={task?.routeType === TmsRouteTypeEnum.Linehaul && task?.businessType === TmsBusinessTypeEnum.OwnRoute}
                    />
                );
            })()}
        </>
    );
};

export default EditTaskDrawer;